import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const Register = () => {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const { lang } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/app/kreation');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }

    if (!formData.password) {
      newErrors.password = 'Passwort ist erforderlich';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Passwort muss mindestens 8 Zeichen haben';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwörter stimmen nicht überein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      await register(formData.name, formData.email, formData.password);
      toast.success('Account erfolgreich erstellt');
      navigate('/app/kreation');
    } catch (err) {
      const message = err.response?.data?.detail || 'Registrierung fehlgeschlagen';
      toast.error(message);
      if (message.includes('E-Mail')) {
        setErrors(prev => ({ ...prev, email: message }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="register-page">
      {/* Header */}
      <header className="w-full px-6 py-4 border-b border-[#1A2B42]">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png" 
              alt="xchannels.ai" 
              className="h-12"
            />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <h1 className="text-3xl font-semibold tracking-tight mb-3">
            {lang === 'de' ? 'Account erstellen' : 'Create Account'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {lang === 'de' ? 'Erstelle einen Account, um loszulegen.' : 'Create an account to get started.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Dein Name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`pl-12 h-12 bg-secondary/50 border-transparent focus:border-primary rounded-lg ${
                    errors.name ? 'border-red-500' : ''
                  }`}
                  data-testid="register-name-input"
                />
              </div>
              {errors.name && (
                <p className="text-sm text-red-400">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="deine@email.de"
                  value={formData.email}
                  onChange={handleChange}
                  className={`pl-12 h-12 bg-secondary/50 border-transparent focus:border-primary rounded-lg ${
                    errors.email ? 'border-red-500' : ''
                  }`}
                  data-testid="register-email-input"
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-400">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Mindestens 8 Zeichen"
                  value={formData.password}
                  onChange={handleChange}
                  className={`pl-12 h-12 bg-secondary/50 border-transparent focus:border-primary rounded-lg ${
                    errors.password ? 'border-red-500' : ''
                  }`}
                  data-testid="register-password-input"
                />
              </div>
              {errors.password && (
                <p className="text-sm text-red-400">{errors.password}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Passwort wiederholen"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`pl-12 h-12 bg-secondary/50 border-transparent focus:border-primary rounded-lg ${
                    errors.confirmPassword ? 'border-red-500' : ''
                  }`}
                  data-testid="register-confirm-password-input"
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-400">{errors.confirmPassword}</p>
              )}
            </div>

            <Button 
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-full gradient-button text-white h-12 disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wird erstellt...
                </>
              ) : (
                <>
                  Account erstellen
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Bereits registriert?{' '}
            <Link 
              to="/login" 
              className="text-primary hover:underline"
              data-testid="login-link"
            >
              Anmelden
            </Link>
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default Register;
